import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Sparkles, Upload, Image, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CHANNELS } from "./constants";
import type { Campaign } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateCampaign: (campaign: Campaign) => void;
}

const CHAT_URL = `${process.env.REACT_APP_BACKEND_URL}/functions/v1/grafton-chat`;

export function CreateCampaignDialog({ open, onOpenChange, onCreateCampaign }: Props) {
  const [form, setForm] = useState({ name: "", channel: "facebook", budget: "", landingUrl: "" });
  const [isOrganic, setIsOrganic] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    toast.success("Image ajoutée !");
  };

  const handleGraftonGenerate = async () => {
    setIsGenerating(true);
    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${""}`,
        },
        body: JSON.stringify({
          messages: [{ role: "user", content: "Génère-moi une campagne marketing performante avec un nom accrocheur, le meilleur canal parmi Facebook/Instagram, Google, LinkedIn, Email ou SEO, et un budget recommandé. Réponds en JSON avec les clés: name, channel (facebook/google/linkedin/email/seo), budget (nombre), landingUrl (suggestion)" }],
          context: {},
          stream: false,
        }),
      });
      const data = await resp.json();
      const reply = data.reply || "";
      
      // Try to extract JSON from AI response
      const jsonMatch = reply.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          setForm({
            name: parsed.name || "Campagne IA",
            channel: parsed.channel || "facebook",
            budget: String(parsed.budget || 500),
            landingUrl: parsed.landingUrl || "",
          });
          toast.success("Grafton IA a généré votre campagne ! 🚀");
        } catch {
          toast.error("Grafton a répondu mais le format n'est pas exploitable.");
        }
      } else {
        toast.info("Grafton a répondu : " + reply.slice(0, 100));
      }
    } catch {
      toast.error("Erreur de connexion avec Grafton IA.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreate = () => {
    if (!form.name) return;
    const campaign: Campaign = {
      id: crypto.randomUUID(),
      name: form.name,
      channel: form.channel,
      status: "draft",
      budget: isOrganic ? 0 : Number(form.budget) || 0,
      spent: 0,
      stage: "draft",
      createdAt: new Date().toISOString().slice(0, 10),
      isOrganic,
      landingUrl: form.landingUrl,
      imageUrl,
      cpc: 0,
      cpl: 0,
      conversions: 0,
      clicks: 0,
    };
    onCreateCampaign(campaign);
    setForm({ name: "", channel: "facebook", budget: "", landingUrl: "" });
    setIsOrganic(false);
    setImageUrl("");
    onOpenChange(false);
    toast.success("Campagne créée !");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer une campagne</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Grafton IA button */}
          <Button
            variant="outline"
            className="w-full gap-2 border-primary/30 hover:bg-primary/5"
            onClick={handleGraftonGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 text-primary" />
            )}
            {isGenerating ? "Grafton réfléchit..." : "Grafton, crée-moi une campagne"}
          </Button>

          <Input
            placeholder="Nom de la campagne"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />

          <Select value={form.channel} onValueChange={(v) => setForm((p) => ({ ...p, channel: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHANNELS.map((ch) => (
                <SelectItem key={ch.id} value={ch.id}>
                  <span className="flex items-center gap-2">
                    <ch.icon className="h-3.5 w-3.5" /> {ch.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Organic toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="organic" className="text-sm flex items-center gap-2">
              🌱 Campagne organique (gratuite)
            </Label>
            <Switch id="organic" checked={isOrganic} onCheckedChange={setIsOrganic} />
          </div>

          {!isOrganic && (
            <Input
              type="number"
              placeholder="Budget (€)"
              value={form.budget}
              onChange={(e) => setForm((p) => ({ ...p, budget: e.target.value }))}
            />
          )}

          {/* Landing URL */}
          <Input
            placeholder="URL de landing page (optionnel)"
            value={form.landingUrl}
            onChange={(e) => setForm((p) => ({ ...p, landingUrl: e.target.value }))}
          />

          {/* Image upload */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              {imageUrl ? "Changer le visuel" : "Uploader un visuel"}
            </Button>
            {imageUrl && (
              <div className="mt-2 rounded-lg overflow-hidden h-24">
                <img src={imageUrl} alt="Visuel" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <Button onClick={handleCreate} className="w-full">
            Créer la campagne
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
